"use client";

import React, { useMemo, useState } from "react";

import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import { Card } from "@/core/components/ui/card";
import { NoTranslate } from "@/core/components/ui/no-translate";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/core/components/ui/dialog-blur";
import type { ValidationButtonProps } from "@/core/components/ui/validation-button";
import { ValidationButton } from "@/core/components/ui/validation-button";
import {
  dialogFullscreenMobileClass,
  dialogPreviewContentClass,
} from "@/core/components/ui/responsive-classes";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { cn } from "@/core/utils/func/cn";
import { globalFormatAmount } from "@/core/utils/func/globalFormatAmount";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import {
  Bug,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Code,
  Copy,
  Download,
  Eye,
  Pencil,
  Share2,
  Trash2,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { DebugView } from "./debug-view";
import {
  isShareablePreviewDebugAllowed,
  resolveShareablePreviewMode,
  type ShareablePreviewMode,
} from "./resolve-preview-mode";
import { UserView } from "./user-view";

export type ShareablePreviewAction = {
  label: string;
  icon?:
    | string
    | React.ComponentType<{ className?: string }>
    | React.ReactElement;
  onClick: () => void;
  variant?: ValidationButtonProps["variant"];
  withPulse?: boolean;
  withIndicator?: boolean;
  buttonClassName?: string;
  disabled?: boolean;
  /** Action asynchrone en cours : spinner affiché + bouton désactivé. */
  loading?: boolean;
  /** Ordre d'affichage (plus petit = à gauche). Défaut : 1. */
  order?: number;
};

/** Ligne de frais (ou total) affichée dans le récapitulatif avant confirmation. */
export interface ShareablePreviewFeeLine {
  label: string;
  amount: string | number;
  currency: string;
}

interface ShareablePreviewProps {
  data: unknown;
  title?: string;
  description?: string;
  actions?: ShareablePreviewAction[];
  shareUrl?: string;
  className?: string;
  /** Cap de hauteur de la carte ≥ `sm`. Ignoré en plein écran mobile (flex). */
  maxHeight?: string;
  mode?: ShareablePreviewMode;
  expandByDefault?: boolean;
  trigger?: React.ReactElement;
  triggerLabel?: string;
  triggerClassName?: string;
  defaultOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * Fonction de rendu (pas un type de composant). Une closure inline
   * `customView={() => ...}` doit rester le même nœud dans l'arbre : la
   * passer à `React.createElement` change le type à chaque rendu et
   * démonte les champs (le motif perd le focus à chaque frappe).
   */
  customView?: (props: {
    data: unknown;
    className?: string;
    expandByDefault?: boolean;
    excludedKeys?: string[];
    excludedNestedKeys?: string[];
    maxDepth?: number;
    [key: string]: unknown;
  }) => React.ReactNode;
  customViewProps?: Record<string, unknown>; // Props supplémentaires à passer au customView
  /** Récapitulatif des frais affiché avant confirmation (règle projet : tout submit passe par une preview avec frais). */
  fees?: ShareablePreviewFeeLine[];
  /** Ligne de total (frais inclus), mise en avant sous la liste des frais. */
  total?: ShareablePreviewFeeLine;
}

const getDataType = (value: unknown): string => {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (typeof value === "string") {
    return value.startsWith("<") && value.includes(">") ? "HTML" : "Text";
  }
  if (Array.isArray(value)) return "Array";
  if (typeof value === "object") return "Object";
  return typeof value;
};

const sanitizeFileName = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "")
    .concat(".txt");

const normalizeActionKey = (value?: string) =>
  value
    ? value
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim()
    : "";

const ACTION_ICON_MAP: Record<string, LucideIcon> = {
  modifier: Pencil,
  edit: Pencil,
  update: Pencil,
  "mise a jour": Pencil,
  maj: Pencil,
  supprimer: Trash2,
  delete: Trash2,
  remove: Trash2,
  retirer: Trash2,
  destroy: Trash2,
  effacer: Trash2,
  partager: Share2,
  share: Share2,
  envoyer: Share2,
  telecharger: Download,
  download: Download,
  exporter: Download,
  voir: Eye,
  afficher: Eye,
  visualiser: Eye,
  preview: Eye,
  annuler: XCircle,
  cancel: XCircle,
  retour: XCircle,
  back: XCircle,
  confirmer: CheckCircle,
  valider: CheckCircle,
  confirm: CheckCircle,
  validate: CheckCircle,
};

const DANGER_KEYWORDS = [
  "supprimer",
  "delete",
  "remove",
  "retirer",
  "destroy",
  "effacer",
  "annuler",
  "cancel",
  "retour",
  "abort",
  "stop",
];

const SUCCESS_KEYWORDS = [
  "confirmer",
  "valider",
  "confirm",
  "validate",
  "approve",
  "approuver",
];

const DEFAULT_ACTION_VARIANT: ValidationButtonProps["variant"] = "blue";

const findIconByKeyword = (key: string): LucideIcon | undefined => {
  if (!key) return undefined;
  if (ACTION_ICON_MAP[key]) return ACTION_ICON_MAP[key];
  const entry = Object.entries(ACTION_ICON_MAP).find(([keyword]) =>
    key.includes(keyword)
  );
  return entry?.[1];
};

export function ShareablePreview({
  data,
  title,
  description,
  actions = [],
  shareUrl,
  className,
  maxHeight,
  mode,
  expandByDefault = true,
  trigger,
  triggerLabel,
  triggerClassName,
  defaultOpen = false,
  onOpenChange,
  customView,
  customViewProps = {},
  fees = [],
  total,
}: ShareablePreviewProps) {
  const { t } = useTypedTranslation();
  // Source unique VITE_NODE_ENV : debug seulement en development.
  // Hors dev, `mode="debug"` est ignoré (forcé user) — pas de 2e paint debug→user.
  const debugAllowed = isShareablePreviewDebugAllowed();
  const resolvedMode = resolveShareablePreviewMode(mode);
  const [isExpanded, setIsExpanded] = useState(expandByDefault);
  const [modeOverride, setModeOverride] = useState<ShareablePreviewMode | null>(
    null
  );
  const [isDialogOpen, setIsDialogOpen] = useState(defaultOpen);
  const currentMode: ShareablePreviewMode = debugAllowed
    ? (modeOverride ?? resolvedMode)
    : "user";

  const formatData = useMemo(
    () =>
      (value: unknown): string => {
        if (value === null || value === undefined) {
          return String(value);
        }
        if (typeof value === "string") {
          try {
            const parsed = JSON.parse(value);
            return JSON.stringify(parsed, null, 2);
          } catch {
            return value;
          }
        }
        if (typeof value === "object") {
          try {
            return JSON.stringify(value, null, 2);
          } catch {
            return String(value);
          }
        }
        return String(value);
      },
    []
  );

  const dataType = useMemo(() => getDataType(data), [data]);

  const dataTypeLabel = useMemo(() => {
    const labels: Record<string, string> = {
      Array: t("shareablePreviewDataTypeArray"),
      Object: t("shareablePreviewDataTypeObject"),
      HTML: t("shareablePreviewDataTypeHtml"),
      Text: t("shareablePreviewDataTypeText"),
      string: t("shareablePreviewDataTypeString"),
      number: t("shareablePreviewDataTypeNumber"),
      boolean: t("shareablePreviewDataTypeBoolean"),
      undefined: t("shareablePreviewDataTypeUndefined"),
      function: t("shareablePreviewDataTypeFunction"),
      symbol: t("shareablePreviewDataTypeSymbol"),
      bigint: t("shareablePreviewDataTypeBigint"),
    };

    return labels[dataType] ?? dataType;
  }, [dataType, t]);

  const handleDialogOpenChange = (nextOpen: boolean) => {
    setIsDialogOpen(nextOpen);
    if (!nextOpen) {
      setIsExpanded(expandByDefault);
      setModeOverride(null);
    }
    onOpenChange?.(nextOpen);
  };

  const handleCopy = async () => {
    if (!("clipboard" in navigator) || !navigator.clipboard?.writeText) {
      notifSilenceError("shareablePreviewClipboardUnsupported");
      return;
    }

    try {
      const textToCopy = formatData(data);
      await navigator.clipboard.writeText(textToCopy);
      notifSilence("shareablePreviewCopySuccess");
    } catch {
      notifSilenceError("shareablePreviewClipboardError");
    }
  };

  const handleShare = async () => {
    if (!("clipboard" in navigator) || !navigator.clipboard?.writeText) {
      notifSilenceError("shareablePreviewClipboardUnsupported");
      return;
    }

    try {
      if (shareUrl) {
        await navigator.clipboard.writeText(shareUrl);
        notifSilence("shareablePreviewShareLinkCopied");
      } else {
        notifSilenceError("shareablePreviewShareUnavailable");
      }
    } catch {
      notifSilenceError("shareablePreviewClipboardError");
    }
  };

  const handleDownload = () => {
    const content = formatData(data);
    if (typeof document === "undefined") {
      notifSilenceError("shareablePreviewDownloadUnavailable");
      return;
    }

    const element = document.createElement("a");
    element.setAttribute(
      "href",
      `data:text/plain;charset=utf-8,${encodeURIComponent(content)}`
    );
    element.setAttribute(
      "download",
      sanitizeFileName(
        title && title.trim().length > 0
          ? title
          : t("shareablePreviewDefaultTitle")
      )
    );
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    notifSilence("shareablePreviewDownloadSuccess");
  };

  const computedTitle =
    title && title.trim().length > 0
      ? title
      : t("shareablePreviewDefaultTitle");

  const dialogTrigger = trigger ?? (
    <Button
      variant="outline"
      size="sm"
      className={cn(
        "flex items-center gap-2 rounded-full border-primary/40 bg-background/80 px-4 py-2 text-sm font-medium text-primary shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary hover:bg-background",
        triggerClassName
      )}
    >
      <Eye className="w-4 h-4" />
      <span>{triggerLabel ?? t("shareablePreviewOpen")}</span>
    </Button>
  );

  // Ne pas afficher le trigger si :
  // 1. Un trigger personnalisé est fourni (on l'affiche)
  // 2. Sinon, si onOpenChange est fourni (contrôle externe) ou defaultOpen est true, on ne l'affiche pas
  const shouldShowTrigger =
    trigger !== undefined || (!onOpenChange && !defaultOpen);

  const sortedActions = useMemo(
    () => [...actions].sort((a, b) => (a.order ?? 1) - (b.order ?? 1)),
    [actions]
  );

  const hasFeesSection = fees.length > 0 || total !== undefined;

  // Jamais d'arbre JSON hors development (même si currentMode/mode disent debug).
  const showDebugTree = debugAllowed && currentMode === "debug";

  return (
    <Dialog open={isDialogOpen} onOpenChange={handleDialogOpenChange}>
      {shouldShowTrigger && (
        <DialogTrigger asChild>{dialogTrigger}</DialogTrigger>
      )}
      <DialogContent
        overlayClassName={cn(
          // Au-dessus d'un Dialog formulaire encore ouvert (preview nested).
          "z-[60]",
          defaultOpen && "data-[state=open]:animate-none"
        )}
        className={cn(
          dialogFullscreenMobileClass,
          dialogPreviewContentClass,
          "z-[60]",
          // twMerge ne déduplique pas toujours les variants `data-[state]`
          // du primitive : important pour que zoom/slide ne déplacent pas le panneau.
          "data-[state=open]:!zoom-in-100 data-[state=closed]:!zoom-out-100",
          "data-[state=open]:!slide-in-from-left-0 data-[state=open]:!slide-in-from-top-0",
          "data-[state=closed]:!slide-out-to-left-0 data-[state=closed]:!slide-out-to-top-0",
          // Montage déjà ouvert (confirmations) : pas d'animation d'entrée.
          defaultOpen && "data-[state=open]:!animate-none"
        )}
      >
        <DialogTitle className="sr-only">{computedTitle}</DialogTitle>
        <DialogDescription className="sr-only">
          {description && description.trim().length > 0
            ? description
            : t("shareablePreviewDescription")}
        </DialogDescription>
        <Card
          className={cn(
            "mt-0 flex h-full min-h-0 min-w-0 flex-col overflow-hidden border border-border/70 bg-card shadow-2xl shadow-foreground/10 max-sm:rounded-none sm:h-auto sm:max-h-[min(var(--shareable-preview-max-h),calc(100dvh-2rem))]",
            "transition-none hover:translate-y-0 hover:shadow-2xl hover:border-border/70",
            className
          )}
          style={
            {
              "--shareable-preview-max-h": maxHeight ?? "90dvh",
            } as React.CSSProperties
          }
        >
          <div className="shrink-0 border-b border-border/50 bg-muted/60 px-3 py-3 pr-12 sm:px-6 sm:py-4 sm:pr-14">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="min-w-0 max-w-full text-base font-semibold leading-snug break-words text-foreground sm:text-lg">
                    {computedTitle}
                  </h3>
                  {debugAllowed && (
                    <span className="shrink-0 whitespace-nowrap rounded-full bg-primary/20 px-2 py-0.5 text-xs font-mono text-primary">
                      {dataTypeLabel}
                    </span>
                  )}
                  {debugAllowed && (
                    <span
                      className={cn(
                        "shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-mono font-medium",
                        currentMode === "debug"
                          ? "bg-warning/20 text-warning"
                          : "bg-info/20 text-info"
                      )}
                    >
                      {currentMode === "debug"
                        ? t("shareablePreviewDebugMode")
                        : t("shareablePreviewUserMode")}
                    </span>
                  )}
                </div>
                {description && (
                  <p className="mt-1 text-xs leading-snug break-words text-muted-foreground sm:text-sm">
                    {description}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                {debugAllowed && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setModeOverride(
                          currentMode === "debug" ? "user" : "debug"
                        )
                      }
                      title={
                        currentMode === "debug"
                          ? t("shareablePreviewSwitchToUser")
                          : t("shareablePreviewSwitchToDebug")
                      }
                      className={cn(
                        currentMode === "debug"
                          ? "text-warning"
                          : "text-info"
                      )}
                    >
                      {currentMode === "debug" ? (
                        <>
                          <Bug className="w-4 h-4" />
                          <span className="hidden ml-1 text-xs sm:inline">
                            {t("shareablePreviewDebugMode")}
                          </span>
                        </>
                      ) : (
                        <>
                          <Code className="w-4 h-4" />
                          <span className="hidden ml-1 text-xs sm:inline">
                            {t("shareablePreviewUserMode")}
                          </span>
                        </>
                      )}
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCopy}
                      title={t("shareablePreviewCopyData")}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </>
                )}

                {shareUrl && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleShare}
                    title={t("shareablePreviewCopyShareLink")}
                  >
                    <Share2 className="w-4 h-4" />
                  </Button>
                )}

                {debugAllowed && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDownload}
                      title={t("shareablePreviewDownloadData")}
                    >
                      <Download className="w-4 h-4" />
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsExpanded((prev) => !prev)}
                      className="ml-auto sm:ml-0"
                      title={
                        isExpanded
                          ? t("shareablePreviewCollapseContent")
                          : t("shareablePreviewExpandContent")
                      }
                    >
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>

          {isExpanded && (
            <div className="show-scrollbar min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain bg-background p-4 sm:p-8">
              {showDebugTree ? (
                <DebugView data={data} className="text-xs" />
              ) : customView ? (
                customView({
                  data,
                  expandByDefault,
                  ...customViewProps,
                })
              ) : (
                <UserView data={data} />
              )}
            </div>
          )}

          {hasFeesSection && (
            <div
              className={cn(
                "shrink-0 border-t border-border/50 bg-muted/40 px-3 py-3 sm:px-6",
                sortedActions.length === 0 &&
                  "pb-[max(0.75rem,env(safe-area-inset-bottom))]"
              )}
            >
              <section
                aria-label={t("common.fees")}
                className="min-w-0 rounded-lg border border-border/70 bg-card p-3 shadow-sm sm:p-4"
              >
                <h4 className="mb-2 text-xs font-bold tracking-wider uppercase text-muted-foreground">
                  {t("common.fees")}
                </h4>
                {fees.length > 0 && (
                  <ul className="divide-y divide-border/50">
                    {fees.map((fee, idx) => (
                      <li
                        key={`${fee.label}-${idx}`}
                        className="flex items-start justify-between gap-3 py-2"
                      >
                        <span className="min-w-0 break-words text-sm text-foreground/80">
                          {fee.label}
                        </span>
                        <NoTranslate
                          as="span"
                          className="shrink-0 text-right text-sm font-semibold text-foreground"
                        >
                          {globalFormatAmount(fee.amount, fee.currency)}
                        </NoTranslate>
                      </li>
                    ))}
                  </ul>
                )}
                {total && (
                  <div
                    className={cn(
                      "flex items-start justify-between gap-3",
                      fees.length > 0 && "mt-2 border-t border-border/70 pt-3"
                    )}
                  >
                    <span className="min-w-0 break-words text-sm font-semibold text-foreground">
                      {total.label}
                    </span>
                    <NoTranslate
                      as="span"
                      className="shrink-0 text-right text-base font-semibold text-accent-dark dark:text-accent-light"
                    >
                      {globalFormatAmount(total.amount, total.currency)}
                    </NoTranslate>
                  </div>
                )}
              </section>
            </div>
          )}

          {sortedActions.length > 0 && (
            <div className="shrink-0 overflow-x-hidden border-t border-border/50 bg-muted/60 px-3 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6">
              <div className="flex w-full min-w-0 flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end sm:gap-3">
                {sortedActions.map((action, idx) => {
                  const {
                    label,
                    icon,
                    onClick,
                    variant,
                    withPulse,
                    withIndicator,
                    buttonClassName,
                    disabled,
                    loading,
                  } = action;

                  const normalizedLabel = normalizeActionKey(label);
                  const normalizedIconHint =
                    typeof icon === "string" ? normalizeActionKey(icon) : "";

                  const isDangerAction = DANGER_KEYWORDS.some(
                    (keyword) =>
                      normalizedLabel.includes(keyword) ||
                      normalizedIconHint.includes(keyword)
                  );

                  const isSuccessAction = SUCCESS_KEYWORDS.some(
                    (keyword) =>
                      normalizedLabel.includes(keyword) ||
                      normalizedIconHint.includes(keyword)
                  );

                  const resolvedVariant =
                    variant ??
                    (isDangerAction
                      ? "red"
                      : isSuccessAction
                        ? "green"
                        : DEFAULT_ACTION_VARIANT);

                  let resolvedIcon:
                    | React.ComponentType<{ className?: string }>
                    | undefined;

                  if (typeof icon === "function") {
                    resolvedIcon = icon;
                  } else if (React.isValidElement(icon)) {
                    const elementWithClassName = icon as React.ReactElement<{
                      className?: string;
                    }>;
                    resolvedIcon = ({ className }: { className?: string }) =>
                      React.cloneElement(elementWithClassName, {
                        className: cn(
                          "w-4 h-4",
                          className,
                          elementWithClassName.props.className
                        ),
                      });
                  } else {
                    resolvedIcon =
                      findIconByKeyword(normalizedIconHint) ??
                      findIconByKeyword(normalizedLabel);
                  }

                  return (
                    <ValidationButton
                      key={`${label}-${idx}`}
                      onClick={onClick}
                      icon={resolvedIcon}
                      variant={resolvedVariant}
                      withPulse={withPulse ?? false}
                      withIndicator={withIndicator ?? false}
                      disabled={disabled || loading}
                      className={cn(
                        "h-auto min-h-11 w-full min-w-0 justify-center whitespace-normal px-4 py-2 text-sm font-semibold uppercase tracking-wide hover:scale-100 sm:w-auto sm:min-w-0 sm:flex-1 md:flex-none md:px-5",
                        buttonClassName,
                        isDangerAction
                          ? "shadow-destructive/30"
                          : isSuccessAction
                            ? "shadow-success/30"
                            : "shadow-primary/20"
                      )}
                    >
                      {loading && <ButtonSpinner />}
                      {label}
                    </ValidationButton>
                  );
                })}
              </div>
            </div>
          )}
        </Card>
      </DialogContent>
    </Dialog>
  );
}
